import {
    ASSISTANT_CANCEL_REASON,
    ASSISTANT_PHASE,
    ASSISTANT_STATUS,
    AssistantCancelReasonCode,
    IAssistantCancelRequest,
    IAssistantMessage,
    IAssistantSession,
} from '../../../models/Assistant';

/**
 * Pure helpers behind the guided "create a storefront from your website" flow.
 *
 * They live apart from the components so the two rules with real consequences, the sub-URL prefill
 * and the cancel-survey reason mapping, can be read and exercised without mounting Fluent.
 */

/**
 * Add https:// when the user typed a bare host.
 *
 * People type "acme.com". Anything that already declares a scheme is left alone, including a
 * non-http one, so the validator below can reject it rather than this quietly turning
 * "ftp://acme.com" into something it is not.
 */
export const normalizeWebsiteUrl = (raw?: string): string => {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return '';
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
};

/**
 * Whether this is plausibly a public website we could go and read.
 *
 * Deliberately shallow: the API is the one that finds out whether the site answers. This only
 * catches the typo class of mistake, a missing dot or a scheme we cannot fetch, before a request
 * is spent on it.
 */
export const isPlausibleWebsiteUrl = (raw?: string): boolean => {
    const value = normalizeWebsiteUrl(raw);
    if (!value) return false;
    let parsed: URL;
    try {
        parsed = new URL(value);
    } catch {
        return false;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    const host = parsed.hostname;
    if (!host) return false;
    if (host === 'localhost') return true;
    // A host worth fetching has at least one dot and a letters-only tail: "acme" is a typo,
    // "acme.com" is a site.
    return /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i.test(host);
};

/** The sub-URL an organization's very first storefront gets offered. */
export const DEFAULT_FIRST_SUB_URL = 'store';

/**
 * The sub-URL to prefill.
 *
 * Only the first storefront gets a suggestion: once an organization has one, "store" is either
 * taken or a bad guess, and an empty field is the honest prompt. `undefined` means the count has
 * not loaded yet, which is also an empty field.
 */
export const defaultSubUrlFor = (storefrontCount?: number): string =>
    storefrontCount === 0 ? DEFAULT_FIRST_SUB_URL : '';

/** The quick-pick chips on the cancel survey, in the order they are shown. */
export const CANCEL_REASON_CHOICES: {key: AssistantCancelReasonCode; text: string}[] = [
    {key: ASSISTANT_CANCEL_REASON.BRAND_MISMATCH, text: 'Did not match my brand'},
    {key: ASSISTANT_CANCEL_REASON.STYLING_BROKEN, text: 'Styling looked wrong or broken'},
    {key: ASSISTANT_CANCEL_REASON.NOT_WHAT_I_ASKED, text: 'Not what I asked for'},
    {key: ASSISTANT_CANCEL_REASON.TOO_SLOW, text: 'Took too long'},
    {key: ASSISTANT_CANCEL_REASON.JUST_EXPLORING, text: 'Just exploring'},
];

/**
 * Turn the survey answers into a cancel body.
 *
 * Rules: a picked chip wins; typing only a comment is recorded as "other" so the answer is not
 * filed under nothing; answering neither produces an empty body, which is a valid cancel. Blank and
 * whitespace-only comments are dropped rather than sent.
 */
export const cancelSurveyBody = (
    reasonCode?: AssistantCancelReasonCode,
    comments?: string
): IAssistantCancelRequest => {
    const text = (comments ?? '').trim();
    const code = reasonCode ?? (text ? ASSISTANT_CANCEL_REASON.OTHER : undefined);
    return {
        ...(code ? {reasonCode: code} : {}),
        ...(text ? {comments: text} : {}),
    };
};

/**
 * The coarse progress copy.
 *
 * This is the fallback for the spinner's label: the session's phase picks the stage, and the stage
 * names what is happening in the user's terms. It is only reached before the turn has executed its
 * first tool, because from then on the label says what the assistant is actually doing.
 */
export interface IGenerationStage {
    /** The ASSISTANT_PHASE value that puts the flow on this stage. */
    phase: string;
    label: string;
}

export const GENERATION_STAGES: IGenerationStage[] = [
    {phase: ASSISTANT_PHASE.DISCOVERY, label: 'Reading your website'},
    {phase: ASSISTANT_PHASE.PROPOSAL, label: 'Designing your storefront'},
    {phase: ASSISTANT_PHASE.APPLYING, label: 'Building your storefront'},
    {phase: ASSISTANT_PHASE.REVIEW, label: 'Adding the finishing touches'},
];

/**
 * Which stage the flow is on.
 *
 * Phase is advisory and a brand-new session often has none yet, so anything unrecognised parks on
 * the first stage rather than showing nothing. Published sits past the last stage, which reads as
 * every step complete.
 */
export const stageIndexFor = (phase?: string): number => {
    if (phase === ASSISTANT_PHASE.PUBLISHED) return GENERATION_STAGES.length;
    const index = GENERATION_STAGES.findIndex((s) => s.phase === phase);
    return index === -1 ? 0 : index;
};

/**
 * What each assistant tool is doing, in the user's language.
 *
 * The keys are the tool names the API registers (AddAssistantTools in
 * AssistantToolServiceCollectionExtensions.cs); keep the two in step when a tool is added. Nothing
 * here names a tool, an endpoint or a field: the operator is told what is happening to their store,
 * which is the same promise the phase copy above makes, only accurate to the moment.
 */
export const TOOL_ACTIVITY_LABELS: Record<string, string> = {
    // Reading what already exists
    list_storefronts: 'Looking over your stores...',
    get_storefront: 'Checking your storefront settings...',
    list_themes: 'Looking through the themes...',
    get_style_schema: 'Working out the styling options...',
    list_banners: 'Reviewing your banners...',
    list_sections: 'Reviewing your home page...',
    list_links: 'Reviewing your header and footer links...',
    list_styles: 'Reviewing your saved styles...',
    suggest_products: 'Finding products for your store...',

    // Out on the public web
    inspect_website: 'Reading your website...',
    capture_storefront: 'Checking how it looks...',
    import_website_image: 'Importing images from your site...',

    // Artwork
    render_asset: 'Drawing artwork for your store...',
    generate_banners: 'Creating your banners...',
    generate_icons: 'Creating your icons...',

    // Writing the storefront
    create_storefront: 'Setting up your storefront...',
    apply_website_theme: 'Matching your website theme...',
    update_branding: 'Applying your branding...',
    update_style_config: 'Styling your storefront...',
    define_style: 'Defining your storefront style...',
    set_localization: 'Setting your wording and currency...',
    set_banners: 'Placing your banners...',
    set_links: 'Wiring up your header and footer links...',
    set_sections: 'Laying out your home page...',
    set_terms: 'Adding your terms and policies...',

    // Catalog
    list_products: 'Looking through your catalog...',
    create_service_products: 'Adding your services to the catalog...',

    // Serving
    generate_preview_link: 'Getting your preview ready...',
    verify_storefront_serving: 'Making sure your store is serving...',

    // Thinking out loud
    propose_design: 'Drafting the design...',
    request_publish: 'Getting your storefront ready for you...',
};

/** What the spinner says about a tool nobody has written copy for yet. */
export const GENERIC_ACTIVITY_LABEL = 'Building your storefront...';

/**
 * The name of the tool most recently seen in the transcript.
 *
 * Scanned from the end, because the last signal is the current one, and both shapes count: the
 * assistant message that asked for the call and the tool message carrying its result. Whichever sits
 * later in the array wins, which is what the single backwards pass gives for free.
 */
const latestToolName = (messages?: IAssistantMessage[]): string | undefined => {
    if (!messages) return undefined;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message?.toolName) return message.toolName;
        const calls = message?.toolCalls;
        if (calls && calls.length > 0) {
            const name = calls[calls.length - 1]?.name;
            if (name) return name;
        }
    }
    return undefined;
};

/**
 * What to tell the operator the assistant is doing right now.
 *
 * The session document is written after every tool execution, so a poll mid-turn sees the calls as
 * they happen and this reads as a live account rather than a timer's guess.
 *
 * A queued session is answered first and never from the transcript: a follow-up turn is Pending with
 * the previous turn's tool calls still sitting at the end of the messages, and reporting those would
 * claim work that is not happening. Only once the turn is running does the transcript speak, and
 * before its first tool call the phase copy stands in.
 */
export const currentActivityLabel = (session?: IAssistantSession): string => {
    if (session?.status === ASSISTANT_STATUS.PENDING) {
        const position = session.queuePosition;
        // One ahead is the one being worked on, which is not worth saying.
        return position != null && position > 1
            ? `Waiting for a slot (${position} ahead)...`
            : 'Starting...';
    }
    const tool = latestToolName(session?.messages);
    if (tool) return TOOL_ACTIVITY_LABELS[tool] ?? GENERIC_ACTIVITY_LABEL;
    const stage = GENERATION_STAGES[Math.min(stageIndexFor(session?.phase), GENERATION_STAGES.length - 1)];
    return `${stage.label}...`;
};
